import React, { useMemo } from 'react';

import { Badge, DataTable } from '@commenda/kakashi';

import { IndirectTaxTransactionHelper } from '../helpers';
import {
  TIndirectTaxTransactionLineItem,
  TIndirectTaxTransactionRegion,
} from '../types';

// ─── Line item columns ───

type LineItemColumn = {
  accessorKey: keyof TIndirectTaxTransactionLineItem;
  header: string;
  headerClassName?: string;
  cell: (info: {
    row: { original: TIndirectTaxTransactionLineItem };
  }) => React.ReactNode;
};

const lineItemProductSkuColumn: LineItemColumn = {
  accessorKey: 'productSku',
  header: 'Product SKU',
  cell: ({ row }) => (
    <span className="txt-small-regular text-text-primary">
      {row.original.productSku}
    </span>
  ),
};

function getLineItemTaxCodeColumn(
  defaultProductTaxCode?: string
): LineItemColumn {
  return {
    accessorKey: 'taxCode',
    header: 'Tax code',
    cell: ({ row }) => {
      const code = row.original.taxCode;
      if (code) {
        return (
          <span className="txt-small-regular text-text-primary">{code}</span>
        );
      }
      if (defaultProductTaxCode) {
        return (
          <Badge variant="blue" size="sm">
            Default - {defaultProductTaxCode}
          </Badge>
        );
      }
      return <span className="txt-small-regular text-text-tertiary">—</span>;
    },
  };
}

const lineItemQuantityColumn: LineItemColumn = {
  accessorKey: 'quantity',
  header: 'Quantity',
  headerClassName: 'w-full text-right',
  cell: ({ row }) => (
    <div className="w-full text-right">
      <span className="txt-small-regular text-text-primary">
        {row.original.quantity}
      </span>
    </div>
  ),
};

function createLineItemCurrencyColumn(
  key: keyof TIndirectTaxTransactionLineItem & string,
  header: string
): LineItemColumn {
  return {
    accessorKey: key,
    header,
    headerClassName: 'w-full text-right',
    cell: ({ row }) => (
      <div className="w-full text-right">
        <span className="txt-small-regular text-text-primary">
          {/* Safe: only called with keys that map to numeric fields */}
          {IndirectTaxTransactionHelper.formatCurrency(
            row.original[key] as number
          )}
        </span>
      </div>
    ),
  };
}

function getLineItemColumnGroups(
  region: TIndirectTaxTransactionRegion,
  onViewProduct?: (productId: string) => void,
  options?: {
    defaultProductTaxCode?: string;
  }
): { id: string; columns: LineItemColumn[] }[] {
  const labels = IndirectTaxTransactionHelper.getLineItemColumnLabels(region);

  const productIdCol: LineItemColumn = {
    accessorKey: 'productId',
    header: 'Product ID',
    cell: ({ row }) => {
      const productId = row.original.productId;
      if (productId && onViewProduct) {
        return (
          <button
            className="txt-small-medium text-text-brand-primary hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              onViewProduct(productId);
            }}
          >
            {productId.substring(0, 8)}...
          </button>
        );
      }
      return (
        <span className="txt-small-regular text-text-quaternary">N/A</span>
      );
    },
  };

  return [
    {
      id: 'lineItems',
      columns: [
        productIdCol,
        lineItemProductSkuColumn,
        getLineItemTaxCodeColumn(options?.defaultProductTaxCode),
        lineItemQuantityColumn,
        createLineItemCurrencyColumn('unitPrice', 'Unit price'),
        createLineItemCurrencyColumn('discount', 'Discount'),
        createLineItemCurrencyColumn('tax', labels.taxLabel),
        createLineItemCurrencyColumn('lineTotal', labels.totalLabel),
      ],
    },
  ];
}

interface IndirectTaxTransactionDetailLineItemsTableProps {
  lineItems: TIndirectTaxTransactionLineItem[];
  region: TIndirectTaxTransactionRegion;
  onRowClick: (lineItem: TIndirectTaxTransactionLineItem) => void;
  onViewProduct?: (productId: string) => void;
  defaultValues?: {
    productTaxCode?: string;
  };
}

const IndirectTaxTransactionDetailLineItemsTable = ({
  lineItems,
  region,
  onRowClick,
  onViewProduct,
  defaultValues,
}: IndirectTaxTransactionDetailLineItemsTableProps) => {

  const columnGroups = useMemo(
    () => getLineItemColumnGroups(region, onViewProduct, { defaultProductTaxCode: defaultValues?.productTaxCode }),
    [region, onViewProduct, defaultValues]
  );

  return (
    <div
      className="flex flex-col gap-lg py-4xl px-3xl"
      data-ai-section="Line items"
    >
      <h2 className="txt-extra-large-bold text-text-primary">Line items</h2>

      <DataTable
        columnGroups={columnGroups}
        data={lineItems}
        tableCellClassName="py-xl"
        tableRowClassName="hover:bg-bg-secondary cursor-pointer"
        onRowClick={onRowClick}
        rowDataAttributes={(row) => ({
          'data-ai-item': 'Line item',
          ...(row.id ? { 'data-ai-id': String(row.id) } : {}),
        })}
      />
    </div>
  );
};

export { IndirectTaxTransactionDetailLineItemsTable };
